import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, AlertCircle, Mail } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicSettings } from '../../contexts/PublicSettingsContext';

// Funnel statuses that indicate the student has already completed onboarding
const ONBOARDING_DONE_STATUSES = ['onboarding_done', 'active', 'payment_expired', 'suspended'];

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Exponential backoff delays: 2s, 4s, 6s, 8s → total ~20s
const POLL_DELAYS = [2000, 4000, 6000, 8000];

export const StudentCheckoutSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const { settings } = usePublicSettings();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('polling'); // polling | confirmed | timeout
  const attemptRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('timeout');
      return;
    }
    poll();
    return () => clearTimeout(timerRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const poll = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/student/billing`);
      if (res.data.active_enrollment) {
        // Enrollment confirmed — refresh user to get latest funnel_status
        await refreshUser();
        setStatus('confirmed');
        // If the student already went through onboarding before (renewal flow),
        // send them to the dashboard instead of repeating onboarding.
        const updatedUser = await axios.get(`${API_URL}/api/auth/me`).catch(() => null);
        const funnelStatus = updatedUser?.data?.funnel_status || '';
        const destination = ONBOARDING_DONE_STATUSES.includes(funnelStatus)
          ? '/student/dashboard'
          : '/student/onboarding';
        timerRef.current = setTimeout(() => navigate(destination), 2500);
        return;
      }
    } catch {
      // ignore network errors during polling
    }

    const attempt = attemptRef.current;
    if (attempt >= POLL_DELAYS.length) {
      setStatus('timeout');
      return;
    }
    attemptRef.current = attempt + 1;
    timerRef.current = setTimeout(poll, POLL_DELAYS[attempt]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAFAF7] via-[#FDF8F3] to-[#FAFAF7] flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl border border-[#E8EAF0] shadow-xl p-10 max-w-md w-full text-center space-y-6"
      >
        <Link to="/">
          <img src="/logo.png" alt="Frankly Talking English" className="h-10 w-auto mx-auto mb-2" />
        </Link>

        {status === 'polling' && (
          <>
            <div className="w-20 h-20 bg-[#1A2E6E]/5 rounded-full flex items-center justify-center mx-auto">
              <Loader2 className="w-10 h-10 text-[#1A2E6E] animate-spin" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1A2E6E] font-['Sora']">Confirming payment…</h1>
              <p className="text-[#1A2E6E]/60 mt-2 text-sm">
                Please wait while we activate your account. This usually takes a few seconds.
              </p>
            </div>
            <div className="flex justify-center gap-1.5 pt-2">
              {POLL_DELAYS.map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i < attemptRef.current ? 'bg-[#1A2E6E]' : 'bg-[#E8EAF0]'
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {status === 'confirmed' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="w-20 h-20 bg-[#4CAF50]/10 rounded-full flex items-center justify-center mx-auto"
            >
              <CheckCircle className="w-10 h-10 text-[#4CAF50]" />
            </motion.div>
            <div>
              <h1 className="text-2xl font-bold text-[#1A2E6E] font-['Sora']">Payment confirmed!</h1>
              <p className="text-[#1A2E6E]/60 mt-2 text-sm">
                Your plan is now active. Redirecting you now…
              </p>
            </div>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-20 h-20 bg-[#F5A623]/10 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle className="w-10 h-10 text-[#F5A623]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1A2E6E] font-['Sora']">Still processing…</h1>
              <p className="text-[#1A2E6E]/60 mt-2 text-sm">
                Your payment was received but activation is taking longer than expected.
                Check your email for confirmation — your account will be activated shortly.
              </p>
            </div>
            <div className="bg-[#F5A623]/5 border border-[#F5A623]/20 rounded-xl p-4 text-left space-y-2 text-sm text-[#1A2E6E]/70">
              <p>Your payment was captured by Stripe</p>
              <p>A confirmation email will be sent to you</p>
              <p>Your account will activate within minutes</p>
            </div>
            <div className="space-y-2">
              <Link
                to="/student/dashboard"
                className="block w-full py-3 rounded-xl bg-[#1A2E6E] text-white font-medium hover:bg-[#16275C] transition-colors"
              >
                Go to Dashboard
              </Link>
              {settings.contact_email && (
                <a
                  href={`mailto:${settings.contact_email}?subject=Payment confirmation`}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border border-[#E8EAF0] text-[#1A2E6E] text-sm hover:bg-[#F5F7FA] transition-colors"
                >
                  <Mail size={14} /> Contact support
                </a>
              )}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default StudentCheckoutSuccess;
